import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { usePageView } from '../../hooks/usePageView';
import { computeSummitSpans } from '../../utils/summitLayout';
import companyImg from './company.png';
import './About.css';

const CDN_HOST = 'https://cdn.sz-showjoy.com';

const DEFAULT_SUMMIT_2026 = {
  year: 2026,
  subtitle_zh: '积极参与全球货代人峰会，拓展国际合作网络',
  subtitle_tw: '積極參與全球貨代人峰會，拓展國際合作網絡',
  subtitle_en: 'Actively participating in global freight forwarding summits to expand our international network',
  description_zh:
    '2026年4月，首嘉/深圳瀚阳团队受邀参加第21届全球货代人峰会（泰国·曼谷），与来自世界各地的物流精英深度交流，共同探索国际货运合作新机遇，进一步拓展全球合作伙伴网络。',
  description_tw:
    '2026年4月，首嘉/深圳瀚陽團隊受邀參加第21屆全球貨代人峰會（泰國·曼谷），與來自世界各地的物流精英深度交流，共同探索國際貨運合作新機遇，進一步拓展全球合作夥伴網絡。',
  description_en:
    'In April 2026, the Showjoy/Shenzhen Hanyang team attended the 21st Global Freight Forwarders Conference in Bangkok, Thailand, engaging in in-depth discussions with logistics professionals from around the world to explore new opportunities in international freight cooperation and expand our global partner network.',
  images: [
    `${CDN_HOST}/uploads/about/summit-1.jpg`,
    `${CDN_HOST}/uploads/about/summit-2.jpg`,
    `${CDN_HOST}/uploads/about/summit-3.jpg`,
    `${CDN_HOST}/uploads/about/summit-4.jpg`,
    `${CDN_HOST}/uploads/about/summit-5.jpg`,
  ],
};

const DESC_KEY_MAP = {
  'zh-CN': 'description_zh',
  'zh-TW': 'description_tw',
  en: 'description_en',
};

const SUB_KEY_MAP = {
  'zh-CN': 'subtitle_zh',
  'zh-TW': 'subtitle_tw',
  en: 'subtitle_en',
};

function About() {
  usePageView('about');
  const { language, t } = useLanguage();
  const a = t.about;

  const [summits, setSummits] = useState([]);
  const [cultureImages, setCultureImages] = useState([]);
  const [yearIndex, setYearIndex] = useState(0);
  const [sliding, setSliding] = useState('');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch('/api/about')
      .then((res) => res.json())
      .then((data) => {
        const apiSummits = Array.isArray(data.summits) ? data.summits : [];
        const has2026 = apiSummits.some((s) => s.year === 2026);
        const merged = has2026
          ? apiSummits
          : [DEFAULT_SUMMIT_2026, ...apiSummits];
        merged.sort((a, b) => b.year - a.year);
        setSummits(merged);
        if (Array.isArray(data.cultureImages)) setCultureImages(data.cultureImages);
      })
      .catch(() => {
        setSummits([DEFAULT_SUMMIT_2026]);
      });
  }, []);

  const descKey = DESC_KEY_MAP[language] || 'description_zh';
  const subKey = SUB_KEY_MAP[language] || 'subtitle_zh';
  const currentSummit = summits[yearIndex] || null;
  const summitSubtitle = currentSummit?.[subKey] || a.summitSubtitle;

  const slideTo = useCallback((nextIndex, direction) => {
    if (sliding) return;
    setSliding(direction);
    setTimeout(() => {
      setYearIndex(nextIndex);
      setSliding('');
    }, 400);
  }, [sliding]);

  useEffect(() => {
    if (paused || summits.length <= 1) return;
    timerRef.current = setInterval(() => {
      setSliding('down');
      setTimeout(() => {
        setYearIndex((prev) => (prev < summits.length - 1 ? prev + 1 : 0));
        setSliding('');
      }, 400);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, [summits.length, paused]);

  return (
    <div className="about-page">
      <Helmet>
        <title>关于我们 - 首嘉/深圳瀚阳</title>
        <meta
          name="description"
          content="了解首嘉/深圳瀚阳的公司背景、发展历程、企业文化与核心优势，专注于专业物流服务。"
        />
        <meta property="og:title" content="关于我们 - 首嘉/深圳瀚阳" />
        <meta property="og:description" content="了解首嘉/深圳瀚阳的公司背景、发展历程、企业文化与核心优势，专注于专业物流服务。" />
        <meta property="og:url" content="https://www.sz-showjoy.com/about" />
        <meta name="keywords" content="首嘉/深圳瀚阳,关于我们,物流公司简介,深圳物流公司,物流企业文化" />
        <link rel="canonical" href="https://www.sz-showjoy.com/about" />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <h1>{a.pageTitle}</h1>
          <p>{a.pageSubtitle}</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="about-intro">
            <div className="about-text">
              <h2>{a.companyIntro}</h2>
              <p>{a.introP1}</p>
              <p>{a.introP2}</p>
            </div>
            <div className="about-image">
              <img src={companyImg} alt="首嘉/深圳瀚阳" className="company-img" />
            </div>
          </div>
        </div>
      </section>

      <section className="section timeline">
        <div className="container">
          <h2 className="section-title">{a.history}</h2>
          <div className="timeline-container">
            <div className="timeline-item">
              <div className="timeline-year">2004</div>
              <div className="timeline-content">
                <h3>{a.founded}</h3>
                <p>{a.foundedDesc}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2007</div>
              <div className="timeline-content">
                <h3>{a.expansion}</h3>
                <p>{a.expansionDesc}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2013</div>
              <div className="timeline-content">
                <h3>{a.upgrade}</h3>
                <p>{a.upgradeDesc}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2019</div>
              <div className="timeline-content">
                <h3>{a.scale}</h3>
                <p>{a.scaleDesc}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2023</div>
              <div className="timeline-content">
                <h3>{a.innovation}</h3>
                <p>{a.innovationDesc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section partners">
        <div className="container">
          <h2 className="section-title">{a.partnersTitle}</h2>
          <p className="section-subtitle">{a.partnersSubtitle}</p>
          <div className="partners-grid">
            {[
              { name: 'JCTrans', id: 'Member ID: 152963' },
              { name: 'IATA', id: 'Member ID: 08338170212' },
              { name: 'GLA', id: 'Member ID: 408591' },
              { name: 'WCA', id: 'Member ID: 147662 & 70311' },
              { name: 'FNC', id: 'Member ID: 85677180' },
              { name: 'PPL', id: 'Member ID: 91585' },
            ].map((p) => (
              <div className="partner-card" key={p.name}>
                <div className="partner-name">{p.name}</div>
                <div className="partner-id">{p.id}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section team">
        <div className="container">
          <h2 className="section-title">{a.advantages}</h2>
          <div className="advantages-grid">
            <div className="advantage-item">
              <h3>{a.wideNetwork}</h3>
              <p>{a.wideNetworkDesc}</p>
            </div>
            <div className="advantage-item">
              <h3>{a.proTeam}</h3>
              <p>{a.proTeamDesc}</p>
            </div>
            <div className="advantage-item">
              <h3>{a.security}</h3>
              <p>{a.securityDesc}</p>
            </div>
            <div className="advantage-item">
              <h3>{a.efficient}</h3>
              <p>{a.efficientDesc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section values">
        <div className="container">
          <h2 className="section-title">{a.culture}</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">🎯</div>
              <h3>{a.mission}</h3>
              <p>{a.missionDesc}</p>
            </div>
            <div className="value-card">
              <div className="value-icon">👁️</div>
              <h3>{a.vision}</h3>
              <p>{a.visionDesc}</p>
            </div>
            <div className="value-card">
              <div className="value-icon">⭐</div>
              <h3>{a.values}</h3>
              <p>{a.valuesDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {summits.length > 0 && (
        <section
          className="section summit"
          // onMouseEnter={() => setPaused(true)}
          // onMouseLeave={() => setPaused(false)}
        >
          <div className="container">
            <h2 className="section-title">{a.summitTitle}</h2>
            <p className="section-subtitle">{summitSubtitle}</p>

            <div className="summit-layout">
              <div className="summit-slider">
                <div className={`summit-slide${sliding ? ` slide-${sliding}` : ''}`}>
                  {currentSummit && (
                    <>
                      {/* <div className="summit-slide-year">{currentSummit.year}</div> */}
                      {currentSummit[descKey] && (
                        <p className="summit-desc">{currentSummit[descKey]}</p>
                      )}
                      {currentSummit.images?.length > 0 && (
                        <div className="summit-grid">
                          {(() => {
                            const spans = computeSummitSpans(currentSummit.images.length);
                            return currentSummit.images.map((url, i) => (
                              <div className="gallery-item" key={i} style={{ gridColumn: `span ${spans[i] || 2}` }}>
                                <img src={url} alt={`${a.summitTitle} ${currentSummit.year} - ${i + 1}`} loading="lazy" />
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {summits.length > 1 && (
                <div className="summit-side-nav">
                  <div className="summit-dots-vertical">
                    {summits.map((s, i) => (
                      <span
                        key={s.year}
                        className={`summit-dot${i === yearIndex ? ' active' : ''}`}
                        onClick={() => slideTo(i, i > yearIndex ? 'down' : 'up')}
                      >
                        {s.year}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {cultureImages.length > 0 && (
        <section className="section culture">
          <div className="container">
            <h2 className="section-title">{a.cultureTitle}</h2>
            <p className="section-subtitle">{a.cultureSubtitle}</p>
            <div className="culture-grid">
              {cultureImages.map((img) => (
                <div className="gallery-item" key={img.id}>
                  <img src={`${CDN_HOST}${img.image_url}`} alt={a.cultureTitle} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default About;
